/**
 * BasketChart — the workspace chart strip showing the WHOLE basket as one candlestick series
 * (combined premium or ₹ P&L), so price action on the position itself can be read the way a
 * single contract's chart is read. Math: basketCandles.ts (pure).
 *
 * Legs = the ones on screen that are part of the position: enabled and not marked CLOSE
 * (held legs at their fills, roll replacements and draft legs at today's price).
 *
 * EXIT LINES (2026-11-02). When the chart shows exactly one open basket (all of its legs,
 * nothing else), its target and stop are drawn on either view and can be DRAGGED. Releasing
 * a line SAVES it at once (owner: no separate confirm step) through the Exit rules endpoint
 * (PUT /options/baskets/:id/levels), which refuses a level the position is already past; an
 * Undo restores the previous level. The stop may be dragged into PROFIT (a floor that locks
 * a minimum gain) as long as it stays below the target.
 *
 * MAX LINES: max profit / max loss of the legs at expiry, fixed (not draggable). Hidden when
 * the legs have different expiries; "unlimited" is said in the footer instead of drawn.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import { createChart, CandlestickSeries, IChartApi, IPriceLine, ISeriesApi, LineStyle, UTCTimestamp } from 'lightweight-charts';
import toast from 'react-hot-toast';
import api from '../../../services/api';
import { optionsService } from '../../../services/options.service';
import { WsLeg } from './wsMath';
import { WsColors, fmtStrike } from './wsUi';
import { formatSignedCurrency, formatPrice } from '../../../utils/formatters';
import { USD_TO_INR } from '../../../utils/constants';
import {
  BcCandle, BcLeg, BcView, combineLegs, entryValue, fineFor, mapCandles, resample, viewMap, withLive,
  exitLevelsV, inverseView, exitSavePatch, ExitLevelsV, expiryExtremesV,
} from './basketCandles';

interface Props {
  legs: WsLeg[];
  cv: number;
  c: WsColors;
  onClose: () => void;
}

const INTERVALS: Array<[number, string]> = [[5, '5m'], [15, '15m'], [60, '1h'], [240, '4h'], [1440, '1D']];
const BARS = 180;
const IST = 19800;
/** How close (px) the pointer must be to an exit line to grab it. */
const GRAB_PX = 7;
const inr = (n: number) => `₹${Math.round(Math.abs(n)).toLocaleString('en-IN')}`;

const BasketChart: React.FC<Props> = ({ legs, cv, c, onClose }) => {
  const hasHeld = legs.some(l => l.held);
  const [interval, setIntervalMin] = useState(60);
  const [view, setView] = useState<BcView>(hasHeld ? 'pnl' : 'premium');
  const [src, setSrc] = useState<'mark' | 'trade'>('mark');
  const [raw, setRaw] = useState<BcCandle[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  const bcLegs: BcLeg[] = useMemo(() => legs.map(l => ({ action: l.action, lots: l.lots, entry: l.price })), [legs]);
  const symKey = legs.map(l => `${l.symbol}:${l.action}:${l.lots}`).join('|');

  // Refetch every minute (fine bars move); the newest bar also follows the live marks below.
  useEffect(() => {
    const t = window.setInterval(() => { if (!document.hidden) setTick(n => n + 1); }, 60000);
    return () => window.clearInterval(t);
  }, []);

  useEffect(() => {
    if (!legs.length) { setRaw(null); return; }
    let alive = true;
    const fine = fineFor(interval);
    const fromSec = Math.floor(Date.now() / 1000) - BARS * interval * 60;
    setLoading(true);
    (async () => {
      try {
        const series: BcCandle[][] = [];
        for (const l of legs) {
          const r = await api.get<any>('/charts/candles', {
            params: { symbol: (src === 'mark' ? 'MARK:' : '') + l.symbol, exchange: 'DELTA', interval: fine, from: fromSec },
            headers: { _suppressToast: 'true' } as any,
          });
          const cs: BcCandle[] = (r.data?.data?.candles || r.data?.candles || []).map((x: any) => ({ time: Number(x.time), open: +x.open, high: +x.high, low: +x.low, close: +x.close }));
          if (!cs.length) throw new Error(`No ${src === 'mark' ? 'mark' : 'trade'} candles for ${l.symbol}${src === 'trade' ? ' — try Mark' : ''}`);
          series.push(cs.sort((a, b) => a.time - b.time));
        }
        if (!alive) return;
        setRaw(combineLegs(bcLegs, series));
        setErr(null);
      } catch (e: any) {
        if (alive) { setErr(e?.response?.data?.error?.message || e?.message || 'Candles unavailable'); setRaw(null); }
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symKey, interval, src, tick]);

  // ── the ONE open basket on the chart (all of its legs and nothing else) ──
  const heldId = useMemo(() => {
    const ids = new Set(legs.map(l => (l.held && l.heldBasketId != null ? l.heldBasketId : -1)));
    return ids.size === 1 && !ids.has(-1) ? Array.from(ids)[0] : null;
  }, [legs]);
  const [bk, setBk] = useState<any>(null);
  // The monitor's cost between the marks and what the basket would really book (exit slippage +
  // entry and exit fees, ₹). Every target / stop is NET of it (2026-11-03).
  const [costInr, setCostInr] = useState(0);
  const [bkTick, setBkTick] = useState(0);
  useEffect(() => {
    if (heldId == null) { setBk(null); setCostInr(0); return; }
    let alive = true;
    optionsService.getBasket(heldId).then((r: any) => {   // GET /baskets/:id → { basket, gates, live }
      if (!alive) return;
      setBk(r.data?.data?.basket || null);
      const c0 = Number(r.data?.data?.live?.costInr);
      setCostInr(Number.isFinite(c0) && c0 > 0 ? c0 : 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [heldId, tick, bkTick]);
  const exitLv: ExitLevelsV | null = useMemo(() => {
    if (!bk || bk.closedAt || bk.id !== heldId) return null;
    const filled = (bk.legFills || []).filter((x: any) => x.filled !== false && x.fillPrice != null).length;
    if (filled !== legs.length) return null;  // a leg marked CLOSE / not shown → different P&L basis
    return exitLevelsV(bk, bcLegs, cv, USD_TO_INR, costInr);
  }, [bk, heldId, legs.length, bcLegs, cv, costInr]);
  const exitHint = heldId == null
    ? (hasHeld ? 'Target / stop lines show when the chart holds exactly one open position and nothing else.' : null)
    : bk && !exitLv ? (legs.length !== (bk.legFills || []).length ? 'Target / stop lines hidden while a leg is marked to close.' : 'This basket has no target / stop to draw.') : null;

  const f = useMemo(() => viewMap(bcLegs, view, cv, USD_TO_INR), [bcLegs, view, cv]);
  const fInv = useMemo(() => inverseView(bcLegs, view, cv, USD_TO_INR), [bcLegs, view, cv]);
  const liveV = useMemo(() => {
    if (!legs.length || legs.some(l => !(Number(l.mark) > 0))) return null;
    return legs.reduce((s, l) => s + (l.action === 'SELL' ? 1 : -1) * l.lots * Number(l.mark), 0);
  }, [legs]);
  const data = useMemo(() => {
    if (!raw) return [];
    const coarse = resample(raw, interval * 60);
    const live = withLive(coarse, liveV, Math.floor(Date.now() / 1000) + IST, interval * 60);
    return mapCandles(live, f);
  }, [raw, interval, liveV, f]);

  const v0 = entryValue(bcLegs);
  const credit = v0 >= 0;
  const entryLine = view === 'pnl' ? 0 : f(v0);
  // Premium decimals follow the size: a 1-lot BTC strangle is worth cents, a big one thousands.
  const pdp = Math.abs(f(v0)) >= 10 ? 2 : Math.abs(f(v0)) >= 1 ? 3 : 4;

  // ── drag state: the line being moved; after release, the level being saved (kept on
  // screen until the basket reloads so the line doesn't flick back) ──
  const [drag, setDrag] = useState<{ which: 'tp' | 'sl'; v: number } | null>(null);
  const [pending, setPending] = useState<{ which: 'tp' | 'sl'; v: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ which: 'tp' | 'sl'; text: string; undo: Record<string, any> } | null>(null);
  useEffect(() => { setPending(null); }, [bk]);
  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(null), 12000);
    return () => window.clearTimeout(t);
  }, [saved]);

  // Max profit / max loss at expiry (one expiry only).
  const extremes = useMemo(() => {
    if (!legs.length || new Set(legs.map(l => l.expiry)).size !== 1) return null;
    return expiryExtremesV(legs.map(l => ({ action: l.action, lots: l.lots, strike: l.strike, optionType: l.optionType })));
  }, [legs]);
  const tpV = drag?.which === 'tp' ? drag.v : pending?.which === 'tp' ? pending.v : exitLv?.tpV;
  const slV = drag?.which === 'sl' ? drag.v : pending?.which === 'sl' ? pending.v : exitLv?.slV;

  // ── chart ──
  const box = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lineRef = useRef<IPriceLine | null>(null);
  const exitRefs = useRef<{ tp: IPriceLine | null; sl: IPriceLine | null; mp: IPriceLine | null; ml: IPriceLine | null }>({ tp: null, sl: null, mp: null, ml: null });
  const fittedFor = useRef<string>('');
  const exitScaleRef = useRef<number[]>([]);
  useEffect(() => {
    if (!box.current) return;
    const ch = createChart(box.current, {
      autoSize: true,
      layout: { background: { color: 'transparent' }, textColor: c.dim as any, fontSize: 11 },
      grid: { vertLines: { color: c.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)' }, horzLines: { color: c.isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)' } },
      rightPriceScale: { borderColor: c.border as any },
      timeScale: { borderColor: c.border as any, timeVisible: true, secondsVisible: false, rightOffset: 4 },
      crosshair: { mode: 0 },
    });
    const s = ch.addSeries(CandlestickSeries, {
      upColor: c.green as any, downColor: c.red as any, borderUpColor: c.green as any, borderDownColor: c.red as any,
      wickUpColor: c.green as any, wickDownColor: c.red as any, priceLineVisible: true,
      // Keep the exit lines on screen even when price is far from them.
      autoscaleInfoProvider: (orig: () => any) => {
        const r = orig();
        const ex = exitScaleRef.current;
        if (!r || !ex.length) return r;
        return { ...r, priceRange: { minValue: Math.min(r.priceRange.minValue, ...ex), maxValue: Math.max(r.priceRange.maxValue, ...ex) } };
      },
    });
    chartRef.current = ch; seriesRef.current = s;
    return () => { ch.remove(); chartRef.current = null; seriesRef.current = null; lineRef.current = null; exitRefs.current = { tp: null, sl: null, mp: null, ml: null }; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.isDark]);

  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    s.setData(data.map(d => ({ ...d, time: d.time as UTCTimestamp })));
    const fmt = view === 'pnl' ? { type: 'custom' as const, formatter: (p: number) => `₹${Math.round(p).toLocaleString('en-IN')}` } : { type: 'price' as const, precision: pdp, minMove: Math.pow(10, -pdp) };
    s.applyOptions({ priceFormat: fmt as any });
    if (lineRef.current) { try { s.removePriceLine(lineRef.current); } catch { /* gone */ } lineRef.current = null; }
    if (data.length) {
      lineRef.current = s.createPriceLine({
        price: entryLine, color: c.amber as any, lineStyle: LineStyle.Dashed, lineWidth: 1, axisLabelVisible: true,
        title: view === 'pnl' ? 'Entry (0)' : `Entry ${credit ? 'credit' : 'debit'}`,
      });
    }
    const key = `${symKey}|${interval}|${view}|${src}`;
    if (data.length && fittedFor.current !== key) { chartRef.current?.timeScale().fitContent(); fittedFor.current = key; }
  }, [data, entryLine, view, credit, c.amber, symKey, interval, src, pdp]);

  const booked = exitLv?.booked ?? 0;
  const totalAt = (v: number) => (v0 - v) * cv * USD_TO_INR + booked;
  const signedInr = (n: number) => `${n >= 0 ? '+' : '−'}${inr(n)}`;
  /** Total-P&L ₹ at a V level (FIXED shows the basket's own unit in the label). */
  const lineTitle = (which: 'tp' | 'sl', v: number) => {
    if (!exitLv) return '';
    const name = which === 'tp' ? 'Target' : 'Stop';
    // Labels show the NET amount (what you receive); the line sits where it fires, costs above.
    if (exitLv.mode === 'FIXED') {
      const total = (v0 - v) * cv * USD_TO_INR + exitLv.booked - exitLv.costInr;
      return `${name} ${total >= 0 ? '+' : '−'}${inr(total)} net${exitLv.booked ? ' total' : ''}`;
    }
    return view === 'pnl' ? `${name} ${formatSignedCurrency(f(v) - exitLv.costInr, 'INR', 0)} net` : `${name} ${formatPrice(f(v), pdp)}`;
  };

  // Exit lines: drawn / moved on every change of level, view or theme.
  useEffect(() => {
    const s = seriesRef.current;
    if (!s) return;
    const draw = (which: 'tp' | 'sl', v: number | undefined) => {
      const cur = exitRefs.current[which];
      if (v == null || !data.length) {
        if (cur) { try { s.removePriceLine(cur); } catch { /* gone */ } exitRefs.current[which] = null; }
        return;
      }
      const opts = {
        price: f(v), color: (which === 'tp' ? c.green : c.red) as any, lineStyle: LineStyle.Dashed, lineWidth: 2 as any,
        axisLabelVisible: true, title: lineTitle(which, v),
      };
      if (cur) cur.applyOptions(opts); else exitRefs.current[which] = s.createPriceLine(opts);
    };
    draw('tp', tpV ?? undefined);
    draw('sl', slV ?? undefined);
    // Fixed reference lines: max profit / max loss at expiry (thin, dotted, not draggable).
    const fixedLine = (key: 'mp' | 'ml', v: number | null | undefined) => {
      const cur = exitRefs.current[key];
      if (v == null || !data.length) {
        if (cur) { try { s.removePriceLine(cur); } catch { /* gone */ } exitRefs.current[key] = null; }
        return;
      }
      const t = totalAt(v);
      const opts = {
        price: f(v), color: (key === 'mp' ? c.green : c.red) as any, lineStyle: LineStyle.Dotted, lineWidth: 1 as any,
        axisLabelVisible: true, title: `${key === 'mp' ? 'Max profit' : 'Max loss'} ${signedInr(t)}${booked ? ' total' : ''}`,
      };
      if (cur) cur.applyOptions(opts); else exitRefs.current[key] = s.createPriceLine(opts);
    };
    fixedLine('mp', extremes?.minV);
    fixedLine('ml', extremes?.maxV);
    exitScaleRef.current = [tpV, slV, extremes?.minV, extremes?.maxV].filter((x): x is number => x != null).map(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tpV, slV, f, data.length, c.green, c.red, exitLv, view, pdp, extremes, booked]);

  // ── dragging: grab within GRAB_PX of a line; the chart does not pan while dragging ──
  const dragRef = useRef<{ which: 'tp' | 'sl' } | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const yOf = (e: PointerEvent) => e.clientY - el.getBoundingClientRect().top;
    const near = (y: number): 'tp' | 'sl' | null => {
      const s = seriesRef.current;
      if (!s || !exitLv || saving) return null;
      let best: 'tp' | 'sl' | null = null, bestD = GRAB_PX + 1;
      for (const which of ['tp', 'sl'] as const) {
        const v = which === 'tp' ? (pending?.which === 'tp' ? pending.v : exitLv.tpV) : (pending?.which === 'sl' ? pending.v : exitLv.slV);
        const yy = s.priceToCoordinate(f(v));
        if (yy == null) continue;
        const d = Math.abs(yy - y);
        if (d < bestD) { bestD = d; best = which; }
      }
      return best;
    };
    const down = (e: PointerEvent) => {
      const which = near(yOf(e));
      if (!which) return;
      e.stopPropagation(); e.preventDefault();
      dragRef.current = { which };
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
      el.style.cursor = 'ns-resize';
    };
    const move = (e: PointerEvent) => {
      const s = seriesRef.current;
      if (!dragRef.current) { el.style.cursor = near(yOf(e)) ? 'ns-resize' : ''; return; }
      const p = s?.coordinateToPrice(yOf(e));
      if (p == null) return;
      setDrag({ which: dragRef.current.which, v: fInv(Number(p)) });
    };
    const up = () => {
      if (!dragRef.current) return;
      const which = dragRef.current.which;
      dragRef.current = null;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      el.style.cursor = '';
      setDrag(d => {
        if (d && d.which === which) commitRef.current(which, d.v);
        return null;
      });
    };
    el.addEventListener('pointerdown', down, true);
    el.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('pointerdown', down, true);
      el.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up);
    };
  }, [exitLv, f, fInv, pending, saving]);

  /** Releasing a dragged line saves it straight away (no confirm step). A refused level
   *  (client check or server) snaps the line back and says why. */
  const commit = async (which: 'tp' | 'sl', v: number) => {
    if (!exitLv || heldId == null || !bk) return;
    const { patch, totalInr, problem } = exitSavePatch(exitLv, which, v, bcLegs, cv, USD_TO_INR);
    if (problem) { toast.error(problem); return; }
    const undo: Record<string, any> = exitLv.mode === 'FIXED'
      ? { exitMode: 'FIXED', tpAmountInr: Number(bk.tpAmountInr), slAmountInr: Number(bk.slAmountInr) }
      : which === 'tp' ? { targetPremium: Number(bk.targetPremium) } : { slPremium: Number(bk.slPremium) };
    const name = which === 'tp' ? 'Target' : 'Stop';
    const text = totalInr != null
      ? `${name} → ${signedInr(totalInr)} net${exitLv.booked ? ' total' : ''} P&L${which === 'sl' && totalInr > 0 ? ' (locks a profit)' : ''}`
      : `${name} → ${formatSignedCurrency((v0 - v) * cv * USD_TO_INR - exitLv.costInr, 'INR', 0)} net on the open legs`;
    setPending({ which, v });
    setSaved(null);
    setSaving(true);
    try {
      await optionsService.updateBasketLevels(heldId, patch as any);
      setSaved({ which, text: `${text} · saved for #${heldId}${bk.tradingMode !== 'PAPER' ? ' (LIVE)' : ''}`, undo });
      setBkTick(n => n + 1);
    } catch (e: any) {
      setPending(null);
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not save', { duration: 7000 });
    } finally { setSaving(false); }
  };
  const commitRef = useRef(commit);
  commitRef.current = commit;
  const undoSave = async () => {
    if (!saved || heldId == null) return;
    setSaving(true);
    try {
      await optionsService.updateBasketLevels(heldId, saved.undo as any);
      setSaved(null);
      toast.success(`Restored the previous ${saved.which === 'tp' ? 'target' : 'stop'} for #${heldId}`);
      setBkTick(n => n + 1);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || e?.message || 'Could not undo', { duration: 7000 });
    } finally { setSaving(false); }
  };

  const seg = (opts: Array<[string, string]>, value: string, set: (v: string) => void, tid: string) => (
    <Box data-testid={tid} sx={{ display: 'flex', bgcolor: c.hover, borderRadius: 1, p: 0.2 }}>
      {opts.map(([k, l]) => (
        <Box key={k} component="button" type="button" data-v={k} onClick={() => set(k)}
          sx={{ all: 'unset', cursor: 'pointer', fontSize: 11, px: 0.7, py: 0.1, borderRadius: 0.8, whiteSpace: 'nowrap',
            fontWeight: value === k ? 700 : 500, color: value === k ? c.text : c.dim, bgcolor: value === k ? c.panel : 'transparent' }}>{l}</Box>
      ))}
    </Box>
  );
  const lastV = data.length ? data[data.length - 1].close : null;
  const legTxt = legs.map(l => `${l.action === 'SELL' ? 'S' : 'B'}${l.lots > 1 ? l.lots : ''} ${fmtStrike(l.strike)}${l.optionType === 'CE' ? 'C' : 'P'}`).join(' · ');
  const isLive = bk && bk.tradingMode !== 'PAPER';

  return (
    <Box data-testid="ws-basket-chart" sx={{ position: 'absolute', inset: 0, zIndex: 30, display: 'flex', flexDirection: 'column', bgcolor: c.panel }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, px: 1, py: 0.4, flexWrap: 'wrap', borderBottom: `1px solid ${c.border}`, fontSize: 11.5 }}>
        <Box sx={{ fontWeight: 800, color: c.amber, whiteSpace: 'nowrap' }}>Basket{heldId != null ? ` #${heldId}` : ''}</Box>
        <Tooltip title={legTxt}><Box sx={{ color: c.dim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 360 }}>{legTxt}</Box></Tooltip>
        {lastV != null && (
          <Box sx={{ fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: view === 'pnl' ? (lastV >= 0 ? c.green : c.red) : c.text }}>
            {view === 'pnl' ? formatSignedCurrency(lastV, 'INR', 0) : `${formatPrice(lastV, pdp)}`}
          </Box>
        )}
        {lastV != null && view === 'pnl' && costInr > 0 && heldId != null && (
          <Tooltip title={`What closing now would book: the P&L at the marks minus ≈${inr(costInr)} of costs (closing at the bid / ask + entry and exit fees). Targets and stops are measured on this net figure.`}>
            <Box data-testid="bc-net" sx={{ fontVariantNumeric: 'tabular-nums', color: lastV - costInr >= 0 ? c.green : c.red, whiteSpace: 'nowrap', cursor: 'help' }}>
              net {formatSignedCurrency(lastV - costInr, 'INR', 0)} <span style={{ color: c.faint }}>after ≈{inr(costInr)} costs</span>
            </Box>
          </Tooltip>
        )}
        {loading && <CircularProgress size={12} />}
        <Box sx={{ flex: 1 }} />
        <Tooltip title={view === 'pnl'
          ? 'P&L in ₹ against each leg\'s entry (held legs: their fills; new legs: today\'s price). Up = profit. Adjustments already booked are not included.'
          : (credit ? 'What it would cost to CLOSE this credit position, in USD for your full size (lots × contract size). Falling = profit.' : 'What this debit position is worth, in USD for your full size (lots × contract size). Rising = profit.')}>
          <span>{seg([['pnl', '₹ P&L'], ['premium', 'Premium']], view, v => setView(v as BcView), 'bc-view')}</span>
        </Tooltip>
        <Tooltip title="Mark: Delta's mark price, continuous on every bar (recommended). Last trade: thin strikes trade rarely, so bars are flat or missing.">
          <span>{seg([['mark', 'Mark'], ['trade', 'Last trade']], src, v => setSrc(v as any), 'bc-src')}</span>
        </Tooltip>
        {seg(INTERVALS.map(([m, l]) => [String(m), l] as [string, string]), String(interval), v => setIntervalMin(Number(v)), 'bc-iv')}
        <Tooltip title="Back to the index chart">
          <Box component="button" type="button" data-testid="ws-basket-close" onClick={onClose} sx={{ all: 'unset', cursor: 'pointer', color: c.dim, px: 0.5, '&:hover': { color: c.text } }}>✕</Box>
        </Tooltip>
      </Box>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
        <Box ref={box} sx={{ position: 'absolute', inset: 0 }} />
        {(saving || saved) && (
          <Box data-testid="ws-exit-saved" sx={{ position: 'absolute', top: 6, left: 8, zIndex: 5, display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.3,
            fontSize: 11.5, borderRadius: 1, border: `1px solid ${c.border}`, bgcolor: saved?.which === 'sl' || pending?.which === 'sl' ? c.redBg : c.greenBg, color: c.text }}>
            {saving && <CircularProgress size={11} />}
            <span>{saving && !saved ? 'Saving…' : saved?.text}</span>
            {saved && !saving && (
              <Box component="button" type="button" data-testid="ws-exit-undo" onClick={undoSave}
                sx={{ all: 'unset', cursor: 'pointer', fontWeight: 700, color: c.blue, '&:hover': { textDecoration: 'underline' } }}>Undo</Box>
            )}
          </Box>
        )}
        {err && <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.dim, fontSize: 12.5, px: 2, textAlign: 'center' }}>{err}</Box>}
        {!err && !loading && raw && !raw.length && (
          <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.dim, fontSize: 12.5 }}>No common history for these legs yet.</Box>
        )}
        <Box sx={{ position: 'absolute', left: 8, bottom: 26, fontSize: 10, color: c.faint, pointerEvents: 'none' }}>
          {exitLv ? `Drag the green target or red stop line — it saves when you let go${isLive ? ' (LIVE)' : ''}; labels are net of costs · ` : exitHint ? `${exitHint} · ` : ''}
          {extremes && (extremes.minV == null || extremes.maxV == null) ? `${[extremes.minV == null ? 'max profit' : '', extremes.maxV == null ? 'max loss' : ''].filter(Boolean).join(' & ')} unlimited · ` : ''}
          {extremes == null && legs.length > 1 ? 'max lines need one expiry · ' : extremes ? 'max lines before costs · ' : ''}
          Open/close exact · highs/lows bounded from {fineFor(interval) === interval ? 'each leg\'s bar' : `${fineFor(interval) >= 60 ? fineFor(interval) / 60 + 'h' : fineFor(interval) + 'm'} leg bars`}
        </Box>
      </Box>
    </Box>
  );
};

export default BasketChart;
